import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../../lib/api";
import { useTenantAuth } from "../../context/auth";

type DashboardKpi = {
  no_show_rate: number;
  today_appointments: number;
  pending_messages: number;
  low_stock_items: number;
  waitlist_open: number;
};

type OpsSnapshot = {
  today_status_counts: {
    pending_confirmation: number;
    confirmed: number;
    completed: number;
    cancelled: number;
    no_show: number;
  };
  upcoming_high_risk: Array<{
    customer_id: string;
    customer_name: string;
    risk_score: number;
    risk_tier: string;
    next_appointment_at: string;
    staff_name?: string | null;
    service_name?: string | null;
  }>;
  queue_health: {
    pending: number;
    failed_24h: number;
    oldest_pending_minutes: number;
  };
};

const QUICK_ACTIONS = [
  { label: "Today", to: "/tg/today" },
  { label: "Calendar", to: "/tg/calendar" },
  { label: "Waitlist", to: "/tg/waitlist" },
  { label: "Clients", to: "/tg/clients" },
  { label: "Book", to: "/tg/book" },
  { label: "Integrations", to: "/tg/integrations" },
];

export function TgDashboardPage() {
  const navigate = useNavigate();
  const { bootstrap } = useTenantAuth();
  const [branchId, setBranchId] = useState("");

  const kpi = useQuery<DashboardKpi>({
    queryKey: ["tg-dashboard-kpi"],
    queryFn: () => apiGet("/v3/analytics/dashboard?window_days=14"),
    refetchInterval: 60_000,
  });

  const ops = useQuery<OpsSnapshot>({
    queryKey: ["tg-dashboard-ops", branchId],
    queryFn: () =>
      apiGet(`/v3/analytics/ops-snapshot?window_days=14${branchId ? `&branch_id=${encodeURIComponent(branchId)}` : ""}`),
    refetchInterval: 60_000,
  });

  const chips = useMemo(
    () => [
      { label: "Today", value: kpi.data?.today_appointments ?? "-" },
      { label: "No-show", value: kpi.data ? `${(kpi.data.no_show_rate * 100).toFixed(1)}%` : "-" },
      { label: "Queue", value: ops.data?.queue_health.pending ?? kpi.data?.pending_messages ?? "-" },
      { label: "Waitlist", value: kpi.data?.waitlist_open ?? "-" },
      { label: "Low stock", value: kpi.data?.low_stock_items ?? "-" },
    ],
    [kpi.data, ops.data?.queue_health.pending]
  );

  return (
    <div className="space-y-4 p-4">
      <div className="rounded-2xl bg-slate-900 p-4 text-white">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-300">Ops Pulse</p>
            <h1 className="mt-1 text-lg font-semibold">{bootstrap?.tenant.name || "Salon"}</h1>
            <p className="text-xs text-slate-300">{bootstrap?.tenant.status || "active"}</p>
          </div>
          <select
            className="rounded-lg border border-white/20 bg-white/10 px-2 py-1 text-xs text-white"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            aria-label="Branch scope"
          >
            <option value="" className="text-black">
              All branches
            </option>
            {(bootstrap?.branches ?? []).map((b) => (
              <option key={b.id} value={b.id} className="text-black">
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {chips.map((chip) => (
          <div key={chip.label} className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">{chip.label}</p>
            <p className="mt-1 text-xl font-semibold text-gray-900">{String(chip.value)}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Quick actions</p>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {QUICK_ACTIONS.map((action) => (
            <button
              key={action.to}
              onClick={() => navigate(action.to)}
              className="rounded-lg border border-teal-200 bg-teal-50 px-2 py-2 text-xs font-semibold text-teal-700"
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Today status mix</p>
        {ops.isLoading && <p className="mt-2 text-sm text-gray-500">Loading...</p>}
        {ops.error && <p className="mt-2 text-sm text-red-600">{String(ops.error)}</p>}
        {ops.data && (
          <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
            <MetricRow label="Pending" value={ops.data.today_status_counts.pending_confirmation} />
            <MetricRow label="Confirmed" value={ops.data.today_status_counts.confirmed} />
            <MetricRow label="Completed" value={ops.data.today_status_counts.completed} />
            <MetricRow label="Cancelled" value={ops.data.today_status_counts.cancelled} />
            <MetricRow label="No-show" value={ops.data.today_status_counts.no_show} />
            <MetricRow label="Queue fail 24h" value={ops.data.queue_health.failed_24h} danger={ops.data.queue_health.failed_24h > 0} />
          </div>
        )}
        {ops.data && (
          <p className="mt-2 text-xs text-gray-500">
            Oldest pending queue item: {ops.data.queue_health.oldest_pending_minutes} min
          </p>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">High-risk upcoming</p>
          <button className="text-xs text-teal-600" onClick={() => navigate("/tg/calendar")}>
            Open calendar
          </button>
        </div>
        <div className="mt-2 space-y-2">
          {(ops.data?.upcoming_high_risk ?? []).slice(0, 5).map((item) => (
            <div key={`${item.customer_id}-${item.next_appointment_at}`} className="rounded-lg border border-gray-100 bg-gray-50 p-2">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900">{item.customer_name}</p>
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                  {item.risk_tier} {item.risk_score}
                </span>
              </div>
              <p className="text-xs text-gray-600">
                {item.service_name || "Service"} · {item.staff_name || "Staff"}
              </p>
              <p className="text-xs text-gray-500">{new Date(item.next_appointment_at).toLocaleString()}</p>
            </div>
          ))}
          {!ops.isLoading && (ops.data?.upcoming_high_risk?.length ?? 0) === 0 && (
            <p className="text-sm text-gray-500">No high-risk upcoming appointments.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricRow({ label, value, danger = false }: { label: string; value: number; danger?: boolean }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-2 py-1.5">
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className={`text-sm font-semibold ${danger ? "text-red-600" : "text-gray-900"}`}>{value}</p>
    </div>
  );
}
